import { Badge, Button, Card, Input, Label, cn } from "@safeplate/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Archive,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  ClipboardPlus,
  ChevronDown,
  FileClock,
  Filter,
  History,
  ImageIcon,
  ExternalLink,
  Paperclip,
  Pencil,
  RefreshCw,
  ShieldAlert,
  UserRound,
  X,
} from "lucide-react";
import { useId, useMemo, useState, type FormEvent } from "react";

import { ApiError, displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import { listStaff } from "../setup/setup-api.js";
import {
  displayRecordValue,
  editRecord,
  fieldLabel,
  getRecord,
  getAttachmentDownload,
  getRecordRevisions,
  getRecordTypes,
  listRecords,
  recordSummary,
  resultTone,
  voidRecord,
  type RecordEntry,
  type RecordFilters,
} from "./records-api.js";

function RecordAttachment({
  attachment,
}: {
  attachment: RecordEntry["attachments"][number];
}) {
  const download = useQuery({
    queryFn: () => getAttachmentDownload(attachment.attachment_id),
    queryKey: ["attachment-download", attachment.attachment_id, "display"],
    refetchInterval: (query) => {
      if (query.state.data) return false;
      const error = query.state.error;
      return !error || (error instanceof ApiError && error.status === 409)
        ? 5_000
        : false;
    },
    retry: false,
  });

  return (
    <figure className="overflow-hidden rounded-2xl border border-stone-200 bg-surface">
      {download.data ? (
        <img
          alt={attachment.caption ?? "Evidence photo"}
          className="max-h-[32rem] w-full bg-stone-100 object-contain"
          src={download.data.download_url}
        />
      ) : (
        <div className="grid min-h-48 place-items-center bg-stone-100 px-5 text-center text-stone-500">
          <div>
            <ImageIcon className="mx-auto size-7" />
            <p className="mt-2 text-sm font-semibold">
              {download.isFetching ? "Preparing photo…" : "Photo is still processing"}
            </p>
            {!download.isFetching ? (
              <Button className="mt-2" onClick={() => void download.refetch()} size="sm" variant="ghost">
                Try again
              </Button>
            ) : null}
          </div>
        </div>
      )}
      <figcaption className="flex items-center gap-3 px-4 py-3">
        <Paperclip className="size-4 shrink-0 text-stone-400" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {attachment.caption ?? "Evidence photo"}
        </span>
        {download.data ? (
          <a
            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800"
            href={download.data.download_url}
            rel="noreferrer"
            target="_blank"
          >
            Full size <ExternalLink className="size-3" />
          </a>
        ) : null}
      </figcaption>
    </figure>
  );
}

function localDateTime(value: string): string {
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatOccurred(value: string): string {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function withOptionalFilter<
  Key extends Exclude<keyof RecordFilters, "includeVoided">,
>(
  filters: RecordFilters,
  key: Key,
  value: NonNullable<RecordFilters[Key]> | undefined,
): RecordFilters {
  const next = { ...filters };
  if (value === undefined) {
    delete next[key];
  } else {
    Object.assign(next, { [key]: value });
  }
  return next;
}

function editableKind(
  key: string,
  value: unknown,
): "boolean" | "json" | "number" | "text" {
  if (
    typeof value === "boolean" ||
    key.startsWith("is_") ||
    key.startsWith("expects_") ||
    key.startsWith("served_")
  )
    return "boolean";
  if (typeof value === "number" || /(_c|_g|_aw|_minutes|_count)$/.test(key))
    return "number";
  if (typeof value === "object" && value !== null) return "json";
  return "text";
}

function parseDetailValue(
  kind: ReturnType<typeof editableKind>,
  raw: string,
): unknown {
  if (raw === "") return null;
  if (kind === "boolean") return raw === "true";
  if (kind === "number") return Number(raw);
  if (kind === "json") return JSON.parse(raw) as unknown;
  return raw;
}

function RecordEditor({
  onClose,
  onSaved,
  record,
}: {
  onClose: () => void;
  onSaved: (record: RecordEntry) => void;
  record: RecordEntry;
}) {
  const id = useId();
  const staff = useQuery({
    queryFn: () => listStaff(record.location_id),
    queryKey: ["staff", record.location_id],
  });
  const source = record.detail ?? record.payload ?? {};
  const storageKey = record.detail ? "detail" : "payload";
  const edit = useMutation({
    mutationFn: (form: FormData) => {
      const changed: Record<string, unknown> = {};
      for (const [key, original] of Object.entries(source)) {
        if (["target_min_c", "target_max_c"].includes(key)) continue;
        const raw = String(form.get(`value.${key}`) ?? "");
        const next = parseDetailValue(editableKind(key, original), raw);
        if (JSON.stringify(next) !== JSON.stringify(original))
          changed[key] = next;
      }
      const nextNote = String(form.get("note") ?? "").trim() || null;
      const nextPerformer = String(form.get("performed_by_staff_id") ?? "");
      const nextOccurred = new Date(
        String(form.get("occurred_at")),
      ).toISOString();
      const input = {
        based_on_revision: record.current_revision,
        ...(Object.keys(changed).length > 0 ? { [storageKey]: changed } : {}),
        ...(nextNote !== record.note ? { note: nextNote } : {}),
        ...(nextPerformer !== record.performed_by_staff_id
          ? { performed_by_staff_id: nextPerformer }
          : {}),
        ...(nextOccurred !== record.occurred_at
          ? { occurred_at: nextOccurred }
          : {}),
        reason: String(form.get("reason") ?? "").trim(),
      };
      return editRecord(record.id, input);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-stone-950/55 p-3 backdrop-blur-sm sm:p-6">
      <form
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="mx-auto my-5 w-full max-w-3xl rounded-3xl bg-surface p-5 shadow-2xl sm:p-7"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          edit.mutate(new FormData(event.currentTarget));
        }}
        role="dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Manager correction
            </p>
            <h2 className="mt-1 text-xl font-black" id={`${id}-title`}>
              Edit {fieldLabel(record.record_type_code)} record
            </h2>
            <p className="mt-2 text-sm text-stone-500">
              The previous values remain in internal history and are never shown
              in the verifier PDF.
            </p>
          </div>
          <Button
            aria-label="Close editor"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor={`${id}-occurred`}>When it happened</Label>
            <Input
              className="mt-1.5"
              defaultValue={localDateTime(record.occurred_at)}
              id={`${id}-occurred`}
              name="occurred_at"
              required
              type="datetime-local"
            />
          </div>
          <div>
            <Label htmlFor={`${id}-performer`}>Performed by</Label>
            <select
              className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3.5 text-sm"
              defaultValue={record.performed_by_staff_id}
              id={`${id}-performer`}
              name="performed_by_staff_id"
            >
              <option value={record.performed_by_staff_id}>
                {staff.data?.find(
                  (person) => person.id === record.performed_by_staff_id,
                )?.display_name ?? "Current performer"}
              </option>
              {staff.data
                ?.filter((person) => person.id !== record.performed_by_staff_id)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name}
                  </option>
                ))}
            </select>
          </div>
          {Object.entries(source)
            .filter(([key]) => !["target_min_c", "target_max_c"].includes(key))
            .map(([key, value]) => {
              const kind = editableKind(key, value);
              const shown =
                kind === "json"
                  ? JSON.stringify(value, null, 2)
                  : value === null || value === undefined
                    ? ""
                    : String(value);
              return (
                <div
                  className={
                    kind === "json" || String(value).length > 80
                      ? "sm:col-span-2"
                      : ""
                  }
                  key={key}
                >
                  <Label htmlFor={`${id}-${key}`}>{fieldLabel(key)}</Label>
                  {kind === "boolean" ? (
                    <select
                      className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3.5 text-sm"
                      defaultValue={String(value ?? false)}
                      id={`${id}-${key}`}
                      name={`value.${key}`}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : kind === "json" || String(value).length > 80 ? (
                    <textarea
                      className="mt-1.5 min-h-24 w-full rounded-xl border border-stone-300 bg-surface px-3.5 py-3 font-mono text-xs"
                      defaultValue={shown}
                      id={`${id}-${key}`}
                      name={`value.${key}`}
                    />
                  ) : (
                    <Input
                      className="mt-1.5"
                      defaultValue={shown}
                      id={`${id}-${key}`}
                      name={`value.${key}`}
                      step={kind === "number" ? "any" : undefined}
                      type={kind === "number" ? "number" : "text"}
                    />
                  )}
                </div>
              );
            })}
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-note`}>Record note</Label>
            <textarea
              className="mt-1.5 min-h-20 w-full rounded-xl border border-stone-300 bg-surface px-3.5 py-3 text-sm"
              defaultValue={record.note ?? ""}
              id={`${id}-note`}
              name="note"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-reason`}>Reason for correction</Label>
            <Input
              className="mt-1.5"
              id={`${id}-reason`}
              name="reason"
              placeholder="Probe was misread; confirmed against calibrated backup"
              required
            />
          </div>
        </div>
        {edit.error ? (
          <div
            className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
            role="alert"
          >
            {edit.error instanceof ApiError &&
            edit.error.code === "revision_conflict"
              ? "Someone changed this record while it was open. Close the editor, review the latest values, then apply your correction again."
              : displayError(edit.error)}
          </div>
        ) : null}
        <div className="mt-7 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={edit.isPending} type="submit">
            {edit.isPending ? "Saving correction…" : "Save correction"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function NestedValue({ name, value }: { name: string; value: unknown }) {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span>None recorded</span>;
    return (
      <div className="space-y-2">
        {value.map((entry, index) => {
          if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
            return <p key={`${name}-${index}`}>{displayRecordValue(entry)}</p>;
          }
          const item = entry as Record<string, unknown>;
          const title =
            typeof item.food_description === "string"
              ? item.food_description
              : `${fieldLabel(name).replace(/s$/, "")} ${index + 1}`;
          return (
            <div className="rounded-xl border border-stone-200 bg-stone-50 p-3" key={String(item.id ?? `${name}-${index}`)}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-bold text-stone-950">{title}</p>
                {typeof item.accepted === "boolean" ? (
                  <Badge tone={item.accepted ? "positive" : "danger"}>
                    {item.accepted ? "Accepted" : "Rejected"}
                  </Badge>
                ) : null}
              </div>
              <dl className="mt-2 grid gap-x-5 gap-y-1 text-xs sm:grid-cols-2">
                {Object.entries(item)
                  .filter(
                    ([key, itemValue]) =>
                      !["id", "supplier_product_id", "food_description", "accepted"].includes(key) &&
                      itemValue !== null &&
                      itemValue !== "",
                  )
                  .map(([key, itemValue]) => (
                    <div className="flex justify-between gap-3" key={key}>
                      <dt className="text-stone-500">{fieldLabel(key)}</dt>
                      <dd className="text-right font-semibold text-stone-800">
                        {displayRecordValue(itemValue)}{key.endsWith("_temp_c") ? " °C" : ""}
                      </dd>
                    </div>
                  ))}
              </dl>
            </div>
          );
        })}
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <dl className="space-y-1">
        {Object.entries(value as Record<string, unknown>).map(([key, nested]) => (
          <div className="flex justify-between gap-3" key={key}>
            <dt className="text-stone-500">{fieldLabel(key)}</dt>
            <dd className="text-right">{displayRecordValue(nested)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return <>{displayRecordValue(value)}</>;
}

function ValueGrid({ values }: { values: Record<string, unknown> }) {
  return (
    <dl className="grid gap-px overflow-hidden rounded-2xl border border-stone-200 bg-stone-200 sm:grid-cols-2">
      {Object.entries(values).map(([key, value]) => (
        <div
          className={cn(
            "min-w-0 bg-surface p-4",
            (Array.isArray(value) ||
              (value !== null && typeof value === "object")) &&
              "sm:col-span-2",
          )}
          key={key}
        >
          <dt className="text-[0.65rem] font-bold tracking-wide text-stone-400 uppercase">
            {fieldLabel(key)}
          </dt>
          <dd className="mt-1.5 overflow-x-auto text-sm font-semibold whitespace-pre-wrap text-stone-900">
            <NestedValue name={key} value={value} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function RecordDrawer({
  canManage,
  onClose,
  recordId,
}: {
  canManage: boolean;
  onClose: () => void;
  recordId: string;
}) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"details" | "history">("details");
  const [editing, setEditing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const record = useQuery({
    queryFn: () => getRecord(recordId),
    queryKey: ["record", recordId],
  });
  const revisions = useQuery({
    enabled: canManage && tab === "history",
    queryFn: () => getRecordRevisions(recordId),
    queryKey: ["record-revisions", recordId],
  });
  const voidMutation = useMutation({
    mutationFn: (reason: string) => voidRecord(recordId, reason),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["record", recordId], updated);
      await queryClient.invalidateQueries({ queryKey: ["records"] });
      setVoiding(false);
    },
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-0 backdrop-blur-sm sm:p-5">
      <section
        aria-label="Record details"
        aria-modal="true"
        className="ml-auto min-h-dvh w-full max-w-4xl bg-surface shadow-2xl sm:min-h-0 sm:rounded-3xl"
        role="dialog"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between border-b border-stone-200 bg-surface/95 px-5 py-5 backdrop-blur sm:rounded-t-3xl sm:px-7">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Evidence record
            </p>
            <h2 className="mt-1 text-xl font-black">
              {record.data
                ? fieldLabel(record.data.record_type_code)
                : "Loading record"}
            </h2>
          </div>
          <Button
            aria-label="Close details"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </header>
        {record.isPending ? (
          <div className="p-7">
            <LoadingState label="Loading record details" />
          </div>
        ) : null}
        {record.error ? (
          <div className="p-7">
            <ErrorState
              message={displayError(record.error)}
              onAction={() => void record.refetch()}
              title="The record isn’t available"
            />
          </div>
        ) : null}
        {record.data ? (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 px-5 py-3 sm:px-7">
              <button
                className={cn(
                  "rounded-lg px-3 py-2 text-xs font-bold",
                  tab === "details"
                    ? "bg-stone-900 text-white"
                    : "text-stone-500 hover:bg-stone-100",
                )}
                onClick={() => setTab("details")}
                type="button"
              >
                Details
              </button>
              {canManage ? (
                <button
                  className={cn(
                    "rounded-lg px-3 py-2 text-xs font-bold",
                    tab === "history"
                      ? "bg-stone-900 text-white"
                      : "text-stone-500 hover:bg-stone-100",
                  )}
                  onClick={() => setTab("history")}
                  type="button"
                >
                  <History className="mr-1.5 inline size-3.5" /> History (
                  {record.data.current_revision})
                </button>
              ) : null}
              <span className="ml-auto">
                <Badge tone={resultTone(record.data.overall_result)}>
                  {record.data.overall_result ?? "not evaluated"}
                </Badge>
              </span>
            </div>
            <div className="space-y-6 p-5 sm:p-7">
              {tab === "details" ? (
                <>
                  {record.data.status === "voided" ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                      <p className="font-bold">
                        This record is voided and excluded from reports.
                      </p>
                      <p className="mt-1">{record.data.voided_reason}</p>
                    </div>
                  ) : null}
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-xl border border-stone-200 bg-surface p-4">
                      <Clock3 className="size-4 text-stone-400" />
                      <p className="mt-2 text-xs font-bold text-stone-400 uppercase">
                        Occurred
                      </p>
                      <p className="mt-1 text-sm font-semibold">
                        {formatOccurred(record.data.occurred_at)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-stone-200 bg-surface p-4">
                      <UserRound className="size-4 text-stone-400" />
                      <p className="mt-2 text-xs font-bold text-stone-400 uppercase">
                        Performed by
                      </p>
                      <p className="mt-1 truncate text-sm font-semibold">
                        {record.data.performed_by_staff_id}
                      </p>
                    </div>
                    <div className="rounded-xl border border-stone-200 bg-surface p-4">
                      <ClipboardCheck className="size-4 text-stone-400" />
                      <p className="mt-2 text-xs font-bold text-stone-400 uppercase">
                        Source
                      </p>
                      <p className="mt-1 text-sm font-semibold capitalize">
                        {record.data.source.replaceAll("_", " ")}
                      </p>
                    </div>
                  </div>
                  <section>
                    <h3 className="mb-3 text-sm font-black">Recorded values</h3>
                    <ValueGrid
                      values={record.data.detail ?? record.data.payload ?? {}}
                    />
                  </section>
                  {record.data.note ? (
                    <section>
                      <h3 className="mb-2 text-sm font-black">Notes</h3>
                      <p className="rounded-xl border border-stone-200 bg-surface p-4 text-sm leading-6 whitespace-pre-wrap text-stone-700">
                        {record.data.note}
                      </p>
                    </section>
                  ) : null}
                  {record.data.attachments.length > 0 ? (
                    <section>
                      <h3 className="mb-3 text-sm font-black">Attachments</h3>
                      <div className="space-y-2">
                        {record.data.attachments.map((attachment) => (
                          <RecordAttachment
                            attachment={attachment}
                            key={attachment.attachment_id}
                          />
                        ))}
                      </div>
                    </section>
                  ) : null}
                  <section>
                    <h3 className="mb-3 text-sm font-black">
                      Rule evaluations
                    </h3>
                    {record.data.evaluations.length > 0 ? (
                      <div className="space-y-2">
                        {record.data.evaluations.map((evaluation) => (
                          <div
                            className="flex items-start gap-3 rounded-xl border border-stone-200 bg-surface p-4"
                            key={`${evaluation.rule_code}-${evaluation.rule_version}`}
                          >
                            <CheckCircle2
                              className={cn(
                                "mt-0.5 size-4 shrink-0",
                                evaluation.outcome === "pass"
                                  ? "text-emerald-700"
                                  : evaluation.outcome === "fail"
                                    ? "text-red-700"
                                    : "text-amber-700",
                              )}
                            />
                            <div>
                              <p className="text-sm font-bold">
                                {fieldLabel(evaluation.rule_code)}
                              </p>
                              <p className="mt-1 text-xs text-stone-500">
                                Version {evaluation.rule_version} ·{" "}
                                {fieldLabel(evaluation.outcome)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-stone-500">
                        No rule evaluation applies to this record.
                      </p>
                    )}
                  </section>
                  {canManage && record.data.status === "active" ? (
                    <div className="flex flex-wrap justify-end gap-2 border-t border-stone-200 pt-5">
                      <Button onClick={() => setVoiding(true)} variant="ghost">
                        <Archive className="size-4" /> Void record
                      </Button>
                      <Button onClick={() => setEditing(true)}>
                        <Pencil className="size-4" /> Correct record
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <section>
                  <h3 className="text-sm font-black">
                    Internal correction history
                  </h3>
                  <p className="mt-1 text-xs leading-5 text-stone-500">
                    This history is for managers and never appears in the
                    verifier PDF.
                  </p>
                  {revisions.isPending ? (
                    <div className="mt-4">
                      <LoadingState label="Loading revision history" />
                    </div>
                  ) : null}
                  {revisions.error ? (
                    <div className="mt-4">
                      <ErrorState
                        message={displayError(revisions.error)}
                        title="History isn’t available"
                      />
                    </div>
                  ) : null}
                  {revisions.data?.length === 0 ? (
                    <div className="mt-4">
                      <EmptyState
                        calm
                        description="The submitted values are still current."
                        title="No corrections"
                      />
                    </div>
                  ) : null}
                  <div className="mt-4 space-y-3">
                    {revisions.data?.map((revision) => (
                      <article
                        className="rounded-2xl border border-stone-200 bg-surface p-4"
                        key={revision.id}
                      >
                        <div className="flex flex-wrap justify-between gap-2">
                          <p className="text-sm font-black">
                            Revision {revision.revision_no}
                          </p>
                          <time className="text-xs text-stone-400">
                            {formatOccurred(revision.changed_at)}
                          </time>
                        </div>
                        <p className="mt-2 text-xs font-semibold text-stone-500">
                          {revision.changed_fields.map(fieldLabel).join(" · ")}
                        </p>
                        {revision.reason ? (
                          <p className="mt-3 rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700">
                            {revision.reason}
                          </p>
                        ) : null}
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <div className="rounded-lg bg-red-50 p-3">
                            <p className="text-[0.65rem] font-bold text-red-700 uppercase">
                              Before
                            </p>
                            <pre className="mt-1 overflow-x-auto text-xs whitespace-pre-wrap text-red-950">
                              {JSON.stringify(
                                revision.previous_values,
                                null,
                                2,
                              )}
                            </pre>
                          </div>
                          <div className="rounded-lg bg-emerald-50 p-3">
                            <p className="text-[0.65rem] font-bold text-emerald-700 uppercase">
                              After
                            </p>
                            <pre className="mt-1 overflow-x-auto text-xs whitespace-pre-wrap text-emerald-950">
                              {JSON.stringify(revision.new_values, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </>
        ) : null}
      </section>
      {editing && record.data ? (
        <RecordEditor
          onClose={() => setEditing(false)}
          onSaved={async (updated) => {
            queryClient.setQueryData(["record", recordId], updated);
            await queryClient.invalidateQueries({ queryKey: ["records"] });
            setEditing(false);
          }}
          record={record.data}
        />
      ) : null}
      {voiding ? (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-stone-950/55 p-4">
          <form
            className="w-full max-w-md rounded-3xl bg-surface p-6 shadow-2xl"
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              voidMutation.mutate(
                String(new FormData(event.currentTarget).get("reason") ?? ""),
              );
            }}
          >
            <ShieldAlert className="size-6 text-red-700" />
            <h3 className="mt-4 text-xl font-black">Void this record?</h3>
            <p className="mt-2 text-sm leading-6 text-stone-500">
              It remains in internal history but is excluded from normal records
              and verifier reports.
            </p>
            <Label className="mt-5 block" htmlFor="void-reason">
              Reason
            </Label>
            <textarea
              className="mt-1.5 min-h-24 w-full rounded-xl border border-stone-300 p-3 text-sm"
              id="void-reason"
              name="reason"
              required
            />
            {voidMutation.error ? (
              <p className="mt-3 text-sm font-semibold text-red-800">
                {displayError(voidMutation.error)}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setVoiding(false)} variant="ghost">
                Cancel
              </Button>
              <Button
                disabled={voidMutation.isPending}
                type="submit"
                variant="destructive"
              >
                {voidMutation.isPending ? "Voiding…" : "Void record"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export function RecordsWorkspacePage() {
  const shell = useShell();
  const locationId = shell.activeLocation?.id;
  const canManage = shell.activeRole !== "staff";
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filters, setFilters] = useState<RecordFilters>({
    includeVoided: false,
  });
  const types = useQuery({
    queryFn: getRecordTypes,
    queryKey: ["record-types"],
    staleTime: 300_000,
  });
  const staff = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listStaff(locationId!),
    queryKey: ["staff", locationId],
  });
  const records = useInfiniteQuery({
    enabled: Boolean(locationId),
    getNextPageParam: (page: Awaited<ReturnType<typeof listRecords>>) =>
      page.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listRecords(locationId!, filters, pageParam),
    queryKey: ["records", locationId, filters],
  });
  const items = useMemo(
    () =>
      records.data?.pages.flatMap(
        (page: Awaited<ReturnType<typeof listRecords>>) => page.items,
      ) ?? [],
    [records.data],
  );

  return (
    <>
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Evidence
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Records
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
            Search the current evidence, inspect rule outcomes, and correct
            mistakes without losing history.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            disabled={records.isFetching}
            onClick={() => void records.refetch()}
            variant="outline"
          >
            <RefreshCw
              className={cn("size-4", records.isFetching && "animate-spin")}
            />{" "}
            Refresh
          </Button>
          <Link
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-emerald-800 px-4 text-sm font-semibold text-white shadow-sm hover:bg-emerald-900"
            to="/log"
          >
            <ClipboardPlus className="size-4" /> New log
          </Link>
        </div>
      </header>
      {!locationId ? (
        <EmptyState
          description="Choose a location to explore its food-safety evidence."
          title="Choose a location"
        />
      ) : (
        <div className="space-y-4">
          <details className="group rounded-xl border border-stone-200 bg-surface">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-4">
              <Filter className="size-4 text-stone-400" />
              <span className="text-sm font-black">Filter evidence</span>
              {Object.entries(filters).some(
                ([key, value]) => key !== "includeVoided" && Boolean(value),
              ) || filters.includeVoided ? (
                <Badge tone="neutral">Active</Badge>
              ) : null}
              <ChevronDown className="ml-auto size-4 text-stone-400 transition group-open:rotate-180" />
            </summary>
            <div className="grid gap-4 border-t border-stone-200 px-4 py-5 sm:grid-cols-2 xl:grid-cols-5">
              <div>
                <Label htmlFor="record-type">Record type</Label>
                <select
                  className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                  id="record-type"
                  onChange={(event) =>
                    setFilters((current) =>
                      withOptionalFilter(
                        current,
                        "recordType",
                        event.target.value || undefined,
                      ),
                    )
                  }
                  value={filters.recordType ?? ""}
                >
                  <option value="">All record types</option>
                  {types.data?.map((type) => (
                    <option key={type.code} value={type.code}>
                      {type.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="record-result">Result</Label>
                <select
                  className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                  id="record-result"
                  onChange={(event) =>
                    setFilters((current) =>
                      withOptionalFilter(
                        current,
                        "outcome",
                        (event.target.value ||
                          undefined) as RecordFilters["outcome"],
                      ),
                    )
                  }
                  value={filters.outcome ?? ""}
                >
                  <option value="">Any result</option>
                  <option value="pass">Pass</option>
                  <option value="warn">Warning</option>
                  <option value="fail">Fail</option>
                  <option value="na">Not applicable</option>
                </select>
              </div>
              {canManage && staff.data?.length ? (
                <div>
                  <Label htmlFor="record-person">Performed by</Label>
                  <select
                    className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                    id="record-person"
                    onChange={(event) =>
                      setFilters((current) =>
                        withOptionalFilter(
                          current,
                          "performer",
                          event.target.value || undefined,
                        ),
                      )
                    }
                    value={filters.performer ?? ""}
                  >
                    <option value="">Anyone</option>
                    {staff.data.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.display_name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-2 xl:col-span-2">
                <div>
                  <Label htmlFor="record-from">From</Label>
                  <Input
                    className="mt-1.5 px-2 text-xs"
                    id="record-from"
                    onChange={(event) =>
                      setFilters((current) =>
                        withOptionalFilter(
                          current,
                          "occurredFrom",
                          event.target.value
                            ? new Date(
                                `${event.target.value}T00:00:00`,
                              ).toISOString()
                            : undefined,
                        ),
                      )
                    }
                    type="date"
                  />
                </div>
                <div>
                  <Label htmlFor="record-to">To</Label>
                  <Input
                    className="mt-1.5 px-2 text-xs"
                    id="record-to"
                    onChange={(event) =>
                      setFilters((current) =>
                        withOptionalFilter(
                          current,
                          "occurredTo",
                          event.target.value
                            ? new Date(
                                `${event.target.value}T23:59:59.999`,
                              ).toISOString()
                            : undefined,
                        ),
                      )
                    }
                    type="date"
                  />
                </div>
              </div>
              <label className="flex cursor-pointer items-end gap-3 py-3 text-sm font-semibold sm:col-span-2 xl:col-span-5">
                <input
                  checked={filters.includeVoided}
                  className="size-4 accent-emerald-800"
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      includeVoided: event.target.checked,
                    }))
                  }
                  type="checkbox"
                />{" "}
                Include voided records
              </label>
            </div>
          </details>
          <div className="min-w-0">
            {records.isPending ? (
              <LoadingState label="Loading records" />
            ) : null}
            {records.error ? (
              <ErrorState
                message={displayError(records.error)}
                onAction={() => void records.refetch()}
                title="Records aren’t available"
              />
            ) : null}
            {!records.isPending && !records.error && items.length === 0 ? (
              <EmptyState
                description="Try a wider date range or different filters; new kitchen submissions appear here after sync."
                title="No matching records"
              />
            ) : null}
            {items.length > 0 ? (
              <Card className="overflow-hidden">
                <div className="divide-y divide-stone-100">
                  {items.map((record) => (
                    <button
                      className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-stone-50 sm:px-5"
                      key={record.id}
                      onClick={() => setSelectedId(record.id)}
                      type="button"
                    >
                      <FileClock className="size-4 shrink-0 text-stone-500" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-black">
                            {types.data?.find(
                              (type) => type.code === record.record_type_code,
                            )?.title ?? fieldLabel(record.record_type_code)}
                          </p>
                          <Badge tone={resultTone(record.overall_result)}>
                            {record.overall_result ?? "not evaluated"}
                          </Badge>
                          {record.status === "voided" ? (
                            <Badge tone="danger">Voided</Badge>
                          ) : null}
                        </div>
                        <p className="mt-1 truncate text-xs text-stone-500">
                          {recordSummary(record)} ·{" "}
                          {formatOccurred(record.occurred_at)}
                        </p>
                      </div>
                      <ChevronRight className="size-4 shrink-0 text-stone-300" />
                    </button>
                  ))}
                </div>
              </Card>
            ) : null}
            {records.hasNextPage ? (
              <div className="mt-4 text-center">
                <Button
                  disabled={records.isFetchingNextPage}
                  onClick={() => void records.fetchNextPage()}
                  variant="outline"
                >
                  {records.isFetchingNextPage
                    ? "Loading…"
                    : "Load older records"}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      )}
      {selectedId ? (
        <RecordDrawer
          canManage={canManage}
          onClose={() => setSelectedId(null)}
          recordId={selectedId}
        />
      ) : null}
    </>
  );
}
